/**
 * AccoursesStreamingService
 *
 * Progressive, batched fetching for the heavy AC-Courses admin lists. Mirrors the
 * TaskStreamingService pattern: a client-side Skip/Take pagination generator that yields one
 * batch (array) per page as it arrives, so the UI can paint rows while later pages are still
 * loading. The backend list endpoints are cache-composed (fast) and offset-paginated.
 */
import { RegistrationService } from './RegistrationService';
import { FacilitatorService } from './FacilitatorService';
export class AccoursesStreamingService {
    constructor(http) {
        this.registrationService = new RegistrationService(http);
        this.facilitatorService = new FacilitatorService(http);
    }
    /**
     * Stream registrations in batches (server offset pagination). Yields one `RegistrationListView[]`
     * per page until the server reports no more rows.
     */
    async *streamRegistrations(filter, chunkSize = 50) {
        let skip = 0;
        let hasMore = true;
        while (hasMore) {
            const response = await this.registrationService.listViewRaw({ ...filter, skip, take: chunkSize });
            const items = response.data?.items ?? [];
            if (items.length > 0)
                yield items;
            if (response.data?.hasMore === false || items.length < chunkSize)
                hasMore = false;
            else
                skip += chunkSize;
        }
    }
    /**
     * Stream facilitators in batches (server offset pagination).
     */
    async *streamFacilitators(filter, chunkSize = 50) {
        let skip = 0;
        let hasMore = true;
        while (hasMore) {
            const response = await this.facilitatorService.listRaw(filter?.statusId, filter?.search, skip, chunkSize);
            const items = response.data ?? [];
            if (items.length > 0)
                yield items;
            if (response.hasMore === false || items.length < chunkSize)
                hasMore = false;
            else
                skip += chunkSize;
        }
    }
    /**
     * Stream the registrations a facilitator is assigned to, in batches. This endpoint returns the
     * full (usually small) set, so it is fetched once and chunked client-side.
     */
    async *streamRegistrationsByFacilitator(userId, chunkSize = 50) {
        const all = await this.facilitatorService.getRegistrations(userId);
        for (let i = 0; i < all.length; i += chunkSize) {
            yield all.slice(i, i + chunkSize);
        }
    }
    /** Collect a whole registration stream into one array, reporting progress per batch. */
    async loadAllRegistrations(filter, chunkSize = 50, onProgress) {
        const all = [];
        for await (const chunk of this.streamRegistrations(filter, chunkSize)) {
            all.push(...chunk);
            onProgress?.(all.length);
        }
        return all;
    }
}
//# sourceMappingURL=AccoursesStreamingService.js.map